import type { GroupDetail } from '@/api/types';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ErrorNote } from '@/ui/ErrorNote';
import { useAnswerOwnershipRequest, useCancelOwnershipTransfer } from './queries';

/**
 * O pedido de propriedade ainda sem resposta. Quem recebeu escolhe aceitar ou recusar; o dono vê que está aguardando e pode
 * cancelar. Enquanto não for aceito, o dono continua sendo o dono. Os demais membros não veem nada.
 */
export function OwnershipRequest({ group }: { group: GroupDetail }) {
  const answer = useAnswerOwnershipRequest(group.id);
  const cancel = useCancelOwnershipTransfer(group.id);
  const request = group.ownershipTransfer;

  if (!request) {
    return null;
  }

  if (request.isForMe) {
    const owner = group.members.find((member) => member.role === 'owner');
    return (
      <Card aria-label="Pedido de propriedade do grupo">
        <h2>Querem passar este grupo para você</h2>
        <p>
          {owner ? `${owner.displayName} pediu` : 'O dono pediu'} para você ser o dono de {group.name}. Se
          você aceitar, vira o dono e {owner ? owner.displayName : 'o dono de antes'} vira administrador. Se
          recusar, nada muda.
        </p>
        <div className="stack-sm">
          <Button
            variant="go"
            icon="key"
            loading={answer.isPending && answer.variables === 'accept'}
            disabled={answer.isPending}
            onClick={() => answer.mutate('accept')}
          >
            Aceitar e virar dono
          </Button>
          <Button
            variant="light"
            loading={answer.isPending && answer.variables === 'decline'}
            disabled={answer.isPending}
            onClick={() => answer.mutate('decline')}
          >
            Recusar
          </Button>
        </div>
        {answer.error ? <ErrorNote error={answer.error} /> : null}
      </Card>
    );
  }

  return (
    <Card aria-label="Pedido de propriedade do grupo">
      <h2>Aguardando {request.toDisplayName}</h2>
      <p>
        Você pediu para {request.toDisplayName} assumir a propriedade do grupo. Até aceitar, você continua
        sendo o dono.
      </p>
      <Button variant="light" size="sm" loading={cancel.isPending} onClick={() => cancel.mutate()}>
        Cancelar o pedido
      </Button>
      {cancel.error ? <ErrorNote error={cancel.error} /> : null}
    </Card>
  );
}
